import React from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { createItem, deleteItem, listItems, updateItem, type InventoryItem } from '../api/inventory.ts';

const InventoryItemsPage: React.FC = () => {
  const [rows, setRows] = React.useState<InventoryItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<InventoryItem | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setRows(await listItems()); } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const cols: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'sku', headerName: 'SKU', width: 140 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'barcode', headerName: 'Barcode', width: 160 },
    { field: 'purchasePrice', headerName: 'Buy', width: 110 },
    { field: 'sellingPrice', headerName: 'Sell', width: 110 },
    { field: 'parLevel', headerName: 'Par', width: 90 },
    {
      field: 'actions', headerName: '', width: 120, renderCell: (p) => (
        <>
          <IconButton onClick={() => { setEditing(p.row as InventoryItem); setOpen(true); }}><EditIcon /></IconButton>
          <IconButton onClick={async () => { await deleteItem((p.row as any).id); load(); }}><DeleteIcon /></IconButton>
        </>
      )
    }
  ];

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Inventory Items</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => { setEditing(null); setOpen(true); }}>Add Item</Button>
      </Box>
      <DataGrid autoHeight rows={rows} columns={cols} loading={loading} getRowId={(r)=> (r.id as any)} />

      <ItemDialog open={open} onClose={() => setOpen(false)} initial={editing} onSaved={load} />
    </Box>
  );
};

export default InventoryItemsPage;

const ItemDialog: React.FC<{ open: boolean; onClose: () => void; initial: InventoryItem | null; onSaved: () => void; }> = ({ open, onClose, initial, onSaved }) => {
  const [form, setForm] = React.useState<InventoryItem>({ sku: '', name: '' });
  React.useEffect(() => { setForm(initial || { sku: '', name: '' }); }, [initial]);

  const save = async () => {
    if (form.id) await updateItem(form.id, form); else await createItem(form);
    onClose(); onSaved();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{form.id ? 'Edit Item' : 'New Item'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={6}><TextField fullWidth label="SKU" value={form.sku||''} onChange={e=> setForm({ ...form, sku:e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Name" value={form.name||''} onChange={e=> setForm({ ...form, name:e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Barcode" value={form.barcode||''} onChange={e=> setForm({ ...form, barcode:e.target.value })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Purchase Price" type="number" value={form.purchasePrice??''} onChange={e=> setForm({ ...form, purchasePrice: Number(e.target.value) })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Selling Price" type="number" value={form.sellingPrice??''} onChange={e=> setForm({ ...form, sellingPrice: Number(e.target.value) })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Tax %" type="number" value={form.taxRate??''} onChange={e=> setForm({ ...form, taxRate: Number(e.target.value) })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Par Level" type="number" value={form.parLevel??''} onChange={e=> setForm({ ...form, parLevel: Number(e.target.value) })} /></Grid>
          <Grid item xs={6}><TextField fullWidth label="Min Qty" type="number" value={form.minQty??''} onChange={e=> setForm({ ...form, minQty: Number(e.target.value) })} /></Grid>
        </Grid>
        <Box display="flex" gap={1} justifyContent="flex-end" mt={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};


